import React from 'react'
import { StyleSheet, ViewProps } from 'react-native'
import { AccessType, Floorplan, useFloorplans } from 'zens-store'
import { Button } from 'zens-storybook'
import { useTranslation } from 'react-i18next'

import { usePermissions } from '@dms/hook'

import { Link } from 'zens-router'

import { Grid, GridItemInfo, GridProps } from '../grid'
import { Panel } from '../layout'

import FloorCard from './card'
import FloorEmpty from './empty'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
})

interface Props extends Omit<GridProps<Floorplan>, 'items' | 'renderItem' | 'data'> {
  companyId: number
  locationId: number
  buildingId: number
}

const FloorGrid = (props: Props) => {
  const { companyId, locationId, buildingId, style, ...otherProps } = props
  const { t } = useTranslation()
  const floorplans = useFloorplans({ companyId, buildingId })
  const { hasPermission } = usePermissions(companyId)

  const items = floorplans.data
    ?.sort((a, b) => (a.number || 0) - (b.number || 0))
    .map((floorplan) => ({
      data: floorplan,
    }))

  const renderFloor = (info: GridItemInfo<Floorplan>) => {
    const { item } = info
    const { data } = item
    return <FloorCard floorplan={data} companyId={companyId} locationId={locationId} buildingId={buildingId} />
  }
  return (
    <Panel
      style={[styles.container, style]}
      title={t('Floors')}
      right={
        items &&
        items.length > 0 && (
          <Link href={`/companies/${companyId}/locations/${locationId}/buildings/${buildingId}/floors/add`} asChild>
            <Button title={t('Add floor')} left="plus" testID="FloorList.Add" />
          </Link>
        )
      }
    >
      {items && items.length > 0 ? (
        hasPermission(AccessType.Create, 'floor') && <Grid renderItem={renderFloor} items={items} {...otherProps} />
      ) : (
        <FloorEmpty companyId={companyId} locationId={locationId} buildingId={buildingId} />
      )}
    </Panel>
  )
}

interface SkeletonProps extends ViewProps {}
const Skeleton = (props: SkeletonProps) => {
  const { style, ...otherProps } = props
  const { t } = useTranslation()
  return (
    <Panel.Skeleton title={t('Floors')} right={<Button.Skeleton />} style={[styles.container, style]} {...otherProps}>
      <Grid.Skeleton itemElement={<FloorCard.Skeleton />} />
    </Panel.Skeleton>
  )
}
FloorGrid.Skeleton = Skeleton

export default FloorGrid
